import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import T2TTopNav from "../Layout/T2TTopNav";
import { getData, postData, deleteData } from "../../../FetchApiServices";
import { useToast, useConfirm } from "../Layout/T2TUIProvider";
import VendorCreationForm from "../Vendor/VendorCreationForm";
import { getAccessMatrix } from "../../../api/t2tVendorFieldAccessApi";

const T2TSubmit = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const toast     = useToast();
  const confirm   = useConfirm();
  const { wfId }  = useParams();
  const vendorId  = new URLSearchParams(location.search).get("vendor_id") || null;

  const [wf, setWf]               = useState(null);
  const [loading, setLoading]     = useState(false);
  const [loadError, setLoadError] = useState("");
  const [formData, setFormData]   = useState({});
  // SG2: access matrix for vendor creation form (initiator = all editable by default)
  const [vendorAccessMatrix, setVendorAccessMatrix] = useState(null);
  const [fileUploads, setFileUploads] = useState({});  // { field_id: [{ file, uploading, uploaded, error, storage_key, original_filename }] }
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted]   = useState(false);
  // Multi-type: selected request type id (when workflow has multiple request types)
  const [selectedRtId, setSelectedRtId] = useState(null);
  // VAL6 — dedup warning state
  const [dupWarning, setDupWarning] = useState(null); // null | { matches: [], pendingSubmit: fn }
  // Review modal
  const [showReview, setShowReview] = useState(false);
  // Priority
  const [priority, setPriority] = useState("medium");
  // Step 9 — Z103 seasonal redirect banner
  const [seasonalBanner, setSeasonalBanner] = useState(false);
  const [draftId, setDraftId]         = useState(null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [lastSaved, setLastSaved]     = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  // Draft picker
  const [draftList, setDraftList]     = useState(null); // null = loading, [] = none, [...] = show picker
  const [draftPicking, setDraftPicking] = useState(false);
  const [lookupResults, setLookupResults] = useState({});  // { fieldKey: [] }
  const [lookupLoading, setLookupLoading] = useState({});  // { fieldKey: bool }
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const lookupTimers = useRef({});

  useEffect(() => {
    fetchWorkflow();
    if (!vendorId) loadDraftList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wfId]);

  // Step 9: Z103 account group auto-redirect to seasonal workflow
  useEffect(() => {
    const isCreation = wf?.request_type?.code === "vendor_creation";
    const isZ103     = String(formData.account_group || "").toUpperCase() === "Z103";
    setSeasonalBanner(isCreation && isZ103);
  }, [formData.account_group, wf]);

  // ── Draft system ──────────────────────────────────────────────────────────────
  const loadDraftList = async () => {
    const resp = await getData(`t2t/requests/drafts?workflow_id=${wfId}`);
    if (!resp.error) {
      const all = Array.isArray(resp.data?.data) ? resp.data.data : [];
      const pending = all.filter((d) => !d.is_submitted);
      if (pending.length === 0) {
        setDraftList([]);   // no drafts — show blank form immediately
      } else {
        setDraftList(pending);
        setDraftPicking(true);  // show picker
      }
    } else {
      setDraftList([]);
    }
  };

  const resumeDraft = (draft) => {
    setDraftId(draft.id || draft.draft_id);
    if (draft.form_data && typeof draft.form_data === "object") {
      setFormData(draft.form_data);
      setDraftLoaded(true);
    }
    setLastSaved(draft.last_saved_at ? new Date(draft.last_saved_at) : null);
    setDraftPicking(false);
  };

  const startFresh = () => {
    setDraftId(null);
    setFormData({});
    setLastSaved(null);
    setDraftLoaded(false);
    setDraftPicking(false);
  };

  const saveDraft = useCallback(async (forceNew = false) => {
    const data = formDataRef.current;
    if (!wfId || Object.keys(data).length === 0) return;
    setDraftSaving(true);
    const resp = await postData("t2t/requests/drafts", {
      workflow_id: Number(wfId),
      form_data:   data,
      ...(!forceNew && draftId && { draft_id: draftId }),
    });
    if (!resp.error) {
      const d = resp.data?.data;
      const newId = d?.id || d?.draft_id;
      if (newId) setDraftId(newId);
      setLastSaved(new Date());
      if (forceNew && newId) {
        // Add the new draft to list
        setDraftList((prev) => [...(prev || []), { id: newId, workflow_id: Number(wfId), form_data: data, last_saved_at: new Date().toISOString() }]);
        toast("Saved as a new draft.", "success");
      }
    }
    setDraftSaving(false);
  }, [wfId, draftId, toast]);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (submitted) return;
    const interval = setInterval(() => {
      if (Object.keys(formDataRef.current).length > 0) saveDraft();
    }, 30000);
    return () => clearInterval(interval);
  }, [saveDraft, submitted]);

  const fetchWorkflow = async () => {
    setLoading(true);
    setLoadError("");
    const resp = await getData(`t2t/workflows/${wfId}`);
    if (resp.error) {
      setLoadError(resp.error.message || "Failed to load workflow.");
    } else {
      const d = resp.data?.data || resp.data || {};
      setWf(d);
      // Initialize selected request type: prefer first in multi-select list, or legacy single
      const allRts = Array.isArray(d.request_types) && d.request_types.length > 0
        ? d.request_types : (d.request_type ? [d.request_type] : []);
      setSelectedRtId(allRts[0]?.request_type_id || null);
      // Fetch access matrix for vendor_creation — submit page user is always initiator
      if (d.request_type?.code === "vendor_creation") {
        const matResp = await getAccessMatrix("create", "initiator");
        if (!matResp.error) setVendorAccessMatrix(matResp.data?.data || null);
      }
      // T2T-03-WF1/WF2: pre-populate form with existing vendor data for change/extend
      if ((d.request_type?.code === "vendor_change" || d.request_type?.code === "vendor_extend") && vendorId) {
        const workflowAction = d.request_type.code === "vendor_extend" ? "extend" : "change";
        const matResp = await getAccessMatrix(workflowAction, "initiator");
        if (!matResp.error) setVendorAccessMatrix(matResp.data?.data || null);
        const vResp = await getData(`t2t/vendors/${vendorId}`);
        if (!vResp.error) {
          const v = vResp.data?.data || {};
          setFormData({ ...v, _vendor_id: vendorId });
        }
      }
    }
    setLoading(false);
  };

  // File upload: get pre-signed URL → upload to S3 → track in state
  const handleFileSelect = async (fieldKey, files) => {
    const fileArr = Array.from(files);
    const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "text/csv"];
    const MAX_SIZE = 25 * 1024 * 1024; // 25 MB

    for (const file of fileArr) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast(`"${file.name}" — file type not allowed. Use PDF, Word, Excel, CSV, or images.`, "error");
        continue;
      }
      if (file.size > MAX_SIZE) {
        toast(`"${file.name}" — file too large. Max 25 MB.`, "error");
        continue;
      }

      const entry = { file, original_filename: file.name, uploading: true, uploaded: false, error: null, storage_key: null };
      setFileUploads((prev) => ({ ...prev, [fieldKey]: [...(prev[fieldKey] || []), entry] }));

      try {
        // 1. Get pre-signed URL
        const presignResp = await postData(`t2t/requests/attachments/presign`, {
          filename:  file.name,
          mime_type: file.type,
          field_id:  fieldKey,
        });
        if (presignResp.error) throw new Error(presignResp.error.message || "Failed to get upload URL.");
        const { presigned_url, storage_key, content_type } = presignResp.data?.data || {};

        // 2. Upload directly to S3 — use content_type from presign response to match S3 signature
        await fetch(presigned_url, { method: "PUT", headers: { "Content-Type": content_type || file.type }, body: file });

        // 3. Mark success in state
        setFileUploads((prev) => ({
          ...prev,
          [fieldKey]: (prev[fieldKey] || []).map((e) =>
            e.file === file ? { ...e, uploading: false, uploaded: true, storage_key } : e
          ),
        }));
      } catch (err) {
        setFileUploads((prev) => ({
          ...prev,
          [fieldKey]: (prev[fieldKey] || []).map((e) =>
            e.file === file ? { ...e, uploading: false, error: err.message } : e
          ),
        }));
        toast(`Upload failed: ${file.name}`, "error");
      }
    }
  };

  const removeFile = (fieldKey, idx) => {
    setFileUploads((prev) => ({
      ...prev,
      [fieldKey]: (prev[fieldKey] || []).filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async (skipConfirm = false) => {
    // Check all file uploads are complete
    const pendingFiles = Object.values(fileUploads).flat().filter((f) => f.uploading);
    if (pendingFiles.length > 0) {
      setSubmitError("Please wait for all file uploads to finish.");
      return;
    }

    // Review modal already served as confirmation — skip inner confirm dialog when called from it
    if (!skipConfirm) {
      const ok = await confirm(
        "Once submitted, this request will be sent for approval and cannot be edited.",
        "Submit this request?"
      );
      if (!ok) return;
    }

    // VAL6 — pre-submit duplicate check for new vendor creation only
    if (wf?.request_type?.code === "vendor_creation" || selectedRtId && wf?.request_types?.find(rt => rt.request_type_id === selectedRtId)?.code === "vendor_creation") {
      const dupResp = await postData("t2t/vendors/check-duplicate", {
        vendor_name: formData.vendor_name || "",
        pan:         formData.pan         || "",
        gstin:       formData.gstin       || "",
      });
      if (!dupResp.error) {
        const matches = dupResp.data?.data || [];
        if (matches.length > 0) {
          // Pause submit — show warning modal; user must confirm or cancel
          setDupWarning({ matches, pendingSubmit: doSubmit });
          return;
        }
      }
    }

    await doSubmit();
  };

  // Extracted submit logic so it can be called from dedup confirm flow
  const doSubmit = async () => {
    // Build attachments array from uploaded files
    const attachments = [];
    Object.entries(fileUploads).forEach(([fieldId, files]) => {
      files.forEach((f) => {
        if (f.uploaded && f.storage_key) {
          attachments.push({ field_id: fieldId, storage_key: f.storage_key, original_filename: f.original_filename });
        }
      });
    });

    setSubmitError("");
    setSubmitting(true);

    // 1. Create the request (without attachments — Node Joi doesn't accept them here)
    const resp = await postData("t2t/requests", {
      workflow_id:     Number(wfId),
      request_type_id: selectedRtId || null,
      title:           wf?.name ? `${wf.name} — ${new Date().toLocaleDateString("en-IN")}` : "New Request",
      form_data:       formData,
      priority,
    });
    if (resp.error) {
      setSubmitError(resp.error.message || "Failed to submit request.");
      setSubmitting(false);
      return;
    }

    // 2. Register attachments separately after request is created
    const requestId = resp.data?.data?.request_id || resp.data?.data?.id || resp.data?.data?.reference_number;
    if (requestId && attachments.length > 0) {
      for (const att of attachments) {
        await postData(`t2t/requests/${requestId}/attachments`, {
          field_id:          att.field_id,
          storage_key:       att.storage_key,
          original_filename: att.original_filename,
        });
      }
    }

    // Remove the draft now that it's been submitted
    if (draftId) {
      deleteData(`t2t/requests/drafts/${draftId}`).catch(() => {});
    }

    setSubmitting(false);
    setSubmitted(true);
    toast("Request submitted successfully!", "success");
    setTimeout(() => navigate("/t2t-requests"), 2000);
  };

  if (submitted) {
    return (
      <>
        <T2TTopNav title="Submit Request" />
        <div className="t2t-content">
          <div style={{ maxWidth: 500, margin: "60px auto", textAlign: "center" }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
            <h2 style={{ fontWeight: 700, marginBottom: 8 }}>Request Submitted!</h2>
            <p style={{ color: "var(--t2t-text-secondary)" }}>Your request has been submitted and is now in progress. Redirecting...</p>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <T2TTopNav title="Submit Request" />
        <div className="t2t-content" style={{ textAlign: "center", paddingTop: 60, color: "var(--t2t-text-muted)", fontSize: 13 }}>Loading...</div>
      </>
    );
  }

  if (loadError || !wf) {
    return (
      <>
        <T2TTopNav title="Submit Request" />
        <div className="t2t-content">
          <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => navigate("/t2t-home")} style={{ marginBottom: 16 }}>← Back</button>
          <div style={{ padding: "10px 14px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12 }}>
            {loadError || "Workflow not found."}
          </div>
        </div>
      </>
    );
  }

  // Compute active request type (supports multi-select)
  const allRts = Array.isArray(wf?.request_types) && wf.request_types.length > 0
    ? wf.request_types : (wf?.request_type ? [wf.request_type] : []);
  const activeRt = allRts.find(rt => rt.request_type_id === selectedRtId) || allRts[0] || null;

  // vendor_creation / vendor_change / vendor_extend all render the 6-tab VendorCreationForm
  const isVendorCreation = ["vendor_creation", "vendor_change", "vendor_extend"].includes(activeRt?.code);

  // form_fields from workflow: array of { field_id, label, field_type } or similar
  // ARCH-02b-i fix-up: if the workflow is attached to a Request Type, the fields
  // come from there (per-workflow fields stay empty in that case). Prefer request
  // type fields when present; fall through to legacy per-workflow fields otherwise.
  const fields = (activeRt && Array.isArray(activeRt.fields) && activeRt.fields.length > 0)
    ? activeRt.fields
    : (Array.isArray(wf.form_fields) ? wf.form_fields
    : Array.isArray(wf.fields)       ? wf.fields
    : []);

  // ── Lookup fields (INT-4/5/6/14): hsn_lookup, sap_lookup, ariba_supplier, gem_product ──
  const LOOKUP_TYPES = ["hsn_lookup", "sap_lookup", "ariba_supplier", "gem_product"];

  const doLookupSearch = (field, searchText) => {
    const key = fieldKey(field);
    const ft = getFieldType(field);
    if (!searchText || searchText.length < (field.min_chars || 2)) { setLookupResults((p) => ({ ...p, [key]: [] })); return; }

    // Debounce 300ms
    if (lookupTimers.current[key]) clearTimeout(lookupTimers.current[key]);
    lookupTimers.current[key] = setTimeout(async () => {
      setLookupLoading((p) => ({ ...p, [key]: true }));
      let url = "";
      if (ft === "hsn_lookup") url = `t2t/lookup/hsn?search=${encodeURIComponent(searchText)}`;
      else if (ft === "sap_lookup") url = `t2t/lookup/sap-odata/${field.sap_object || "A_Product"}?search=${encodeURIComponent(searchText)}&field=${field.sap_search_field || "Product"}`;
      else if (ft === "ariba_supplier") url = `t2t/lookup/ariba-suppliers?search=${encodeURIComponent(searchText)}`;
      else if (ft === "gem_product") url = `t2t/lookup/gem-products?search=${encodeURIComponent(searchText)}`;

      const resp = await getData(url);
      if (!resp.error) {
        const raw = resp.data?.data;
        setLookupResults((p) => ({ ...p, [key]: Array.isArray(raw) ? raw : [] }));
      }
      setLookupLoading((p) => ({ ...p, [key]: false }));
    }, 300);
  };

  const selectLookupItem = (field, item) => {
    const key = fieldKey(field);
    const ft = getFieldType(field);
    let value = "";
    let extraData = {};

    if (ft === "hsn_lookup") {
      value = item.hsn_code;
      extraData = { [`${key}_description`]: item.description, [`${key}_gst_rate`]: item.gst_rate_percent };
    } else if (ft === "sap_lookup") {
      value = item[field.sap_value_field || "Product"] || item.value || Object.values(item)[0] || "";
    } else if (ft === "ariba_supplier") {
      value = item.ariba_supplier_id || item.supplier_name || "";
      extraData = { [`${key}_name`]: item.supplier_name };
    } else if (ft === "gem_product") {
      value = item.product_id || item.name || "";
      extraData = { [`${key}_name`]: item.name || item.description };
    }

    setFormData({ ...formData, [key]: value, ...extraData });
    setLookupResults((p) => ({ ...p, [key]: [] }));
  };

  const isTextarea = (f) => {
    const id = String(f.field_id || f.id || f.name || "").toLowerCase();
    const type = String(f.field_type || f.type || "").toLowerCase();
    return type === "textarea" || id.includes("justification") || id.includes("reason") || id.includes("description");
  };

  const isFile = (f) => {
    const type = String(f.field_type || f.type || "").toLowerCase();
    return type === "file" || type === "file_upload";
  };

  const getFieldType = (f) => String(f.field_type || f.type || "text").toLowerCase();
  const fieldKey  = (f) => String(f.field_id || f.id || f.name || "");
  const fieldLabel = (f) => String(f.label || f.name || f.field_id || "");
  const fieldOptions = (f) => Array.isArray(f.options) ? f.options : [];

  // Conditional field visibility — hide field if depends_on condition is not met
  const isFieldVisible = (field) => {
    if (!field.depends_on) return true;
    const dep = field.depends_on;
    const depKey = String(dep.field_id || "");
    const depVal = formData[depKey];
    const target = dep.value;
    switch (dep.operator) {
      case "equals":
      case "==":  return String(depVal) === String(target);
      case "not_equals":
      case "!=":  return String(depVal) !== String(target);
      case "in":  return Array.isArray(target) && target.map(String).includes(String(depVal));
      case "not_empty": return depVal !== undefined && depVal !== null && depVal !== "";
      case "empty":     return !depVal;
      default:          return String(depVal) === String(target);
    }
  };

  return (
    <>
      <T2TTopNav title="Submit Request" />
      <div className="t2t-content">
        <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => navigate("/t2t-home")} style={{ marginBottom: 16 }}>
          ← Back
        </button>
        <div className="t2t-submit-wrap">
          <div className="t2t-submit-head">
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--t2t-text-muted)", marginBottom: 6 }}>
              New Request
            </div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{wf.name}</div>
            {wf.description && (
              <div style={{ fontSize: 12, color: "var(--t2t-text-secondary)", marginTop: 4 }}>{wf.description}</div>
            )}
            {/* Multi-type picker: only shown when workflow supports >1 request type */}
            {allRts.length > 1 && (
              <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--t2t-text-muted)" }}>Request Type:</span>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {allRts.map(rt => (
                    <button
                      key={rt.request_type_id}
                      type="button"
                      onClick={() => setSelectedRtId(rt.request_type_id)}
                      style={{
                        padding: "4px 12px", fontSize: 12, borderRadius: 20, border: "1px solid",
                        cursor: "pointer", fontWeight: 600,
                        borderColor: selectedRtId === rt.request_type_id ? "var(--t2t-primary)" : "#CBD5E1",
                        background: selectedRtId === rt.request_type_id ? "var(--t2t-primary)" : "white",
                        color: selectedRtId === rt.request_type_id ? "white" : "var(--t2t-text)",
                      }}
                    >
                      {rt.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="t2t-submit-body">
            {submitError && (
              <div style={{ padding: "8px 12px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{submitError}</div>
            )}

            {/* Draft resumed banner */}
            {draftLoaded && (
              <div style={{ padding: "10px 14px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, marginBottom: 16, display: "flex", alignItems: "center", gap: 12, fontSize: 13 }}>
                <i className="bi bi-clock-history" style={{ color: "#2563EB", fontSize: 16 }} />
                <span style={{ flex: 1, color: "#1E40AF" }}>
                  <strong>Draft resumed.</strong> Your previously saved data has been loaded.
                </span>
                <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ color: "#64748B", whiteSpace: "nowrap" }}
                  onClick={() => setDraftPicking(true)}>
                  Switch draft
                </button>
                <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ color: "#DC2626", whiteSpace: "nowrap" }}
                  onClick={startFresh}>
                  Start fresh
                </button>
              </div>
            )}

            {/* Step 9: Z103 seasonal redirect banner */}
            {seasonalBanner && (
              <div style={{ padding: "12px 16px", background: "#FFF3CD", border: "1px solid #FDE68A", borderRadius: 8, marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 18 }}>🌾</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#92400E" }}>Seasonal Account Group Detected (Z103)</div>
                  <div style={{ fontSize: 12, color: "#78350F", marginTop: 2 }}>
                    Z103 uses a fast-track 2-level workflow (Initiator → MDM only). Switch to the Seasonal Vendor workflow for the correct approval chain.
                  </div>
                </div>
                <button
                  className="t2t-btn t2t-btn-primary t2t-btn-sm"
                  onClick={() => navigate(`/t2t-requests?new=vendor_creation_seasonal`)}
                >
                  Switch to Seasonal Workflow →
                </button>
              </div>
            )}

            {/* Vendor Creation — 6-tab structured form with SG2 access matrix */}
            {isVendorCreation && (
              <VendorCreationForm
                formData={formData}
                onChange={(key, value) => setFormData((prev) => ({ ...prev, [key]: value }))}
                readOnly={false}
                accessMatrix={vendorAccessMatrix}
                requestTypeCode={activeRt?.code}
              />
            )}

            {/* BUG-66: Supporting Documents for vendor creation — generic flow already
                renders file fields per request-type config, but the hardcoded
                VendorCreationForm doesn't. Synthetic field_id "vendor_documents"
                groups KYC / PAN / GST / bank-statement uploads in one place. */}
            {isVendorCreation && (() => {
              const docKey = "vendor_documents";
              const uploads = fileUploads[docKey] || [];
              return (
                <div className="t2t-fg" style={{ marginTop: 16 }}>
                  <label>
                    <i className="bi bi-paperclip me-1" /> Supporting Documents
                    <span style={{ fontSize: 11, color: "var(--t2t-text-muted)", fontWeight: 400, marginLeft: 6 }}>
                      (optional)
                    </span>
                  </label>
                  <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginBottom: 6 }}>
                    Attach KYC, PAN, GST certificate, bank statement, or any other supporting files.
                  </div>
                  <div
                    style={{ border: "2px dashed var(--t2t-border)", borderRadius: 8, padding: 16, textAlign: "center", cursor: "pointer", background: "#FAFBFC" }}
                    onClick={() => document.getElementById(`file-${docKey}`)?.click()}
                  >
                    <div style={{ fontSize: 24, marginBottom: 4 }}>📎</div>
                    <div style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>Click to browse or drag files here</div>
                    <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 4 }}>PDF, Word, Excel, CSV, JPG, PNG — max 25 MB each</div>
                    <input
                      id={`file-${docKey}`}
                      type="file"
                      multiple
                      style={{ display: "none" }}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png"
                      onChange={(e) => { handleFileSelect(docKey, e.target.files); e.target.value = ""; }}
                    />
                  </div>
                  {uploads.length > 0 && (
                    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                      {uploads.map((u, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "6px 10px", background: u.error ? "#FEF2F2" : u.uploaded ? "#F0FDF4" : "#F8FAFC", borderRadius: 6, border: `1px solid ${u.error ? "#FECACA" : u.uploaded ? "#BBF7D0" : "#E2E8F0"}` }}>
                          <span>{u.uploaded ? "✅" : u.uploading ? "⏳" : "❌"}</span>
                          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.original_filename}</span>
                          {u.uploading && <span style={{ fontSize: 10, color: "#64748B" }}>Uploading...</span>}
                          {u.error && <span style={{ fontSize: 10, color: "#DC2626" }}>{u.error}</span>}
                          <button onClick={() => removeFile(docKey, i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 14 }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Generic flat field list for non-vendor workflows */}
            {!isVendorCreation && fields.length === 0 && (
              <div style={{ padding: "16px 0", fontSize: 12, color: "var(--t2t-text-muted)" }}>
                This workflow has no form fields defined yet.
              </div>
            )}

            {!isVendorCreation && fields.map((field) => {
              if (!isFieldVisible(field)) return null;
              const key = fieldKey(field);
              if (isFile(field)) {
                const uploads = fileUploads[key] || [];
                return (
                  <div className="t2t-fg" key={key}>
                    <label>{fieldLabel(field)}</label>
                    <div
                      style={{ border: "2px dashed var(--t2t-border)", borderRadius: 8, padding: 16, textAlign: "center", cursor: "pointer", background: "#FAFBFC" }}
                      onClick={() => document.getElementById(`file-${key}`)?.click()}
                    >
                      <div style={{ fontSize: 24, marginBottom: 4 }}>📎</div>
                      <div style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>Click to browse or drag files here</div>
                      <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 4 }}>PDF, Word, Excel, CSV, JPG, PNG — max 25 MB each</div>
                      <input
                        id={`file-${key}`}
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png"
                        onChange={(e) => { handleFileSelect(key, e.target.files); e.target.value = ""; }}
                      />
                    </div>
                    {uploads.length > 0 && (
                      <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                        {uploads.map((u, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "6px 10px", background: u.error ? "#FEF2F2" : u.uploaded ? "#F0FDF4" : "#F8FAFC", borderRadius: 6, border: `1px solid ${u.error ? "#FECACA" : u.uploaded ? "#BBF7D0" : "#E2E8F0"}` }}>
                            <span>{u.uploaded ? "✅" : u.uploading ? "⏳" : "❌"}</span>
                            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.original_filename}</span>
                            {u.uploading && <span style={{ fontSize: 10, color: "#64748B" }}>Uploading...</span>}
                            {u.error && <span style={{ fontSize: 10, color: "#DC2626" }}>{u.error}</span>}
                            <button onClick={() => removeFile(key, i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 14 }}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              const ft = getFieldType(field);
              const opts = fieldOptions(field);

              // Lookup fields (hsn_lookup, sap_lookup, ariba_supplier, gem_product)
              if (LOOKUP_TYPES.includes(ft)) {
                const results = lookupResults[key] || [];
                const isSearching = lookupLoading[key];
                return (
                  <div className="t2t-fg" key={key} style={{ position: "relative" }}>
                    <label>{fieldLabel(field)}</label>
                    <input
                      className="t2t-fc"
                      placeholder={ft === "hsn_lookup" ? "Search HSN code or description..." : ft === "sap_lookup" ? `Search ${field.sap_object || "SAP"}...` : ft === "ariba_supplier" ? "Search supplier..." : "Search product..."}
                      value={formData[key] || ""}
                      onChange={(e) => { setFormData({ ...formData, [key]: e.target.value }); doLookupSearch(field, e.target.value); }}
                      autoComplete="off"
                    />
                    {isSearching && <div style={{ position: "absolute", right: 10, top: 28, fontSize: 10, color: "#94A3B8" }}>Searching...</div>}
                    {results.length > 0 && (
                      <div style={{ position: "absolute", left: 0, right: 0, top: "100%", zIndex: 50, background: "#fff", border: "1px solid var(--t2t-border)", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,.12)", maxHeight: 200, overflowY: "auto", marginTop: 2 }}>
                        {results.map((item, idx) => {
                          let label = "";
                          let sub = "";
                          if (ft === "hsn_lookup") { label = item.hsn_code; sub = `${item.description} — GST ${item.gst_rate_percent}%`; }
                          else if (ft === "sap_lookup") {
                            const displayFields = field.sap_display_fields || Object.keys(item).slice(0, 3);
                            label = displayFields.map((f) => item[f]).filter(Boolean).join(" — ");
                          }
                          else if (ft === "ariba_supplier") { label = item.supplier_name; sub = `${item.qualification_status || ""} · ${item.country || ""}`; }
                          else if (ft === "gem_product") { label = item.name || item.product_id; sub = item.description || ""; }
                          return (
                            <div key={idx} style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #F1F5F9", fontSize: 12 }}
                              onClick={() => selectLookupItem(field, item)}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "#F8FAFC"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}>
                              <div style={{ fontWeight: 600 }}>{label}</div>
                              {sub && <div style={{ fontSize: 10, color: "#64748B", marginTop: 1 }}>{sub}</div>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {ft === "hsn_lookup" && formData[`${key}_gst_rate`] && (
                      <div style={{ fontSize: 10, color: "#16A34A", marginTop: 4 }}>GST Rate: {formData[`${key}_gst_rate`]}%</div>
                    )}
                  </div>
                );
              }

              return (
                <div className="t2t-fg" key={key}>
                  <label>{fieldLabel(field)}</label>

                  {/* Dropdown / Select / Radio — ARCH-02b-iii: accept both "dropdown" (legacy) and "select"/"radio" (Request Types) */}
                  {ft === "dropdown" || ft === "select" || ft === "radio" ? (
                    <select
                      className="t2t-fc"
                      value={formData[key] || ""}
                      onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    >
                      <option value="">Select {fieldLabel(field)}...</option>
                      {opts.map((opt) => {
                        const val = typeof opt === "object" ? (opt.value || opt.label || "") : opt;
                        const lbl = typeof opt === "object" ? (opt.label || opt.value || "") : opt;
                        return <option key={val} value={val}>{lbl}</option>;
                      })}
                    </select>

                  /* Yes/No Toggle — "checkbox" kept for legacy; Request Types use it as multi-select which isn't supported here yet. */
                  ) : ft === "yesno" || ft === "yes_no" || ft === "boolean" || ft === "checkbox" ? (
                    <select
                      className="t2t-fc"
                      value={formData[key] || ""}
                      onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    >
                      <option value="">Select...</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>

                  /* Textarea */
                  ) : isTextarea(field) ? (
                    <textarea
                      className="t2t-fc" rows="3"
                      placeholder={`Enter ${fieldLabel(field)}...`}
                      value={formData[key] || ""}
                      onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    />

                  /* Default: text / number / date / currency / email */
                  ) : (
                    <input
                      className="t2t-fc"
                      type={ft === "number" || ft === "currency" ? "number"
                          : ft === "date"  ? "date"
                          : ft === "email" ? "email"
                          : ft === "tel"   ? "tel"
                          : "text"}
                      placeholder={field.placeholder || `Enter ${fieldLabel(field)}...`}
                      value={formData[key] || ""}
                      onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    />
                  )}
                </div>
              );
            })}

            {/* Priority selector — compact inline row above Submit (BUG-56) */}
            <div className="t2t-submit-priority-row">
              <span className="t2t-submit-priority-label">
                <i className="bi bi-flag-fill" aria-hidden="true" /> Priority:
              </span>
              <div className="t2t-submit-priority-chips" role="radiogroup" aria-label="Request priority">
                {[
                  { value: "critical", label: "Critical", color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" },
                  { value: "high",     label: "High",     color: "#D97706", bg: "#FFF7ED", border: "#FED7AA" },
                  { value: "medium",   label: "Medium",   color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE" },
                  { value: "low",      label: "Low",      color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" },
                ].map((p) => {
                  const selected = priority === p.value;
                  return (
                    <button
                      key={p.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setPriority(p.value)}
                      className="t2t-submit-priority-chip"
                      style={{
                        border: `1px solid ${selected ? p.color : p.border}`,
                        background: selected ? p.bg : "#fff",
                        color: selected ? p.color : "#64748B",
                        fontWeight: selected ? 700 : 500,
                      }}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Draft save buttons */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
              <button className="t2t-btn t2t-btn-outline" style={{ flex: 1 }}
                onClick={() => saveDraft(false)} disabled={draftSaving}>
                {draftSaving ? "Saving…" : <><i className="bi bi-floppy me-1" />Save Draft</>}
              </button>
              <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ whiteSpace: "nowrap" }}
                onClick={() => saveDraft(true)} disabled={draftSaving}
                title="Save current data as a separate new draft">
                <i className="bi bi-plus-circle me-1" />New Draft
              </button>
              {lastSaved && (
                <span style={{ fontSize: 11, color: "#94A3B8", width: "100%", textAlign: "right" }}>
                  Last saved: {lastSaved.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
            </div>
            <button
              className="t2t-btn t2t-btn-primary t2t-btn-lg t2t-btn-block"
              onClick={() => setShowReview(true)}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Review & Submit →"}
            </button>
          </div>
        </div>
      </div>

      {/* Draft picker modal */}
      {draftPicking && (
        <DraftPicker
          drafts={draftList || []}
          onResume={resumeDraft}
          onFresh={startFresh}
          onClose={() => setDraftPicking(false)}
        />
      )}

      {/* Review & Submit modal */}
      {showReview && (
        <ReviewModal
          formData={formData}
          isVendorCreation={isVendorCreation}
          fields={fields}
          fieldLabel={fieldLabel}
          fieldKey={fieldKey}
          priority={priority}
          onClose={() => setShowReview(false)}
          onConfirm={() => { setShowReview(false); handleSubmit(true); }}
        />
      )}

      {/* VAL6 — Duplicate warning modal */}
      {dupWarning && (
        <div className="t2t-overlay" onClick={() => setDupWarning(null)}>
          <div className="t2t-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="t2t-modal-head">
              <div>
                <div className="t2t-modal-title">⚠️ Potential Duplicate Vendor</div>
                <div className="t2t-modal-sub">The following existing vendor(s) closely match this submission</div>
              </div>
              <button className="t2t-close-btn" onClick={() => setDupWarning(null)}>×</button>
            </div>
            <div className="t2t-modal-body" style={{ maxHeight: 340, overflowY: "auto" }}>
              {dupWarning.matches.map((m) => (
                <div key={m.vendor_id} style={{ padding: "10px 14px", borderBottom: "1px solid #F1F5F9", fontSize: 13 }}>
                  <div style={{ fontWeight: 700, marginBottom: 3 }}>{m.name}</div>
                  <div style={{ color: "#64748B", fontSize: 12, display: "flex", gap: 16, flexWrap: "wrap" }}>
                    {m.vendor_number && <span>#{m.vendor_number}</span>}
                    {m.pan   && <span>PAN: {m.pan}</span>}
                    {m.gstin && <span>GSTIN: {m.gstin}</span>}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    {m.matched_on.map((field) => (
                      <span key={field} style={{ fontSize: 10, fontWeight: 700, background: "#FEF9C3", color: "#92400E", borderRadius: 4, padding: "2px 6px", marginRight: 4 }}>
                        {field.toUpperCase()} MATCH {field === "name" ? `(${m.match_score}%)` : "(Exact)"}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="t2t-modal-foot" style={{ gap: 10 }}>
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setDupWarning(null)}>
                ← Cancel &amp; Fix Form
              </button>
              <button
                className="t2t-btn t2t-btn-primary"
                onClick={async () => { setDupWarning(null); await dupWarning.pendingSubmit(); }}
              >
                Confirm Different → Submit Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── Draft Picker ─────────────────────────────────────────────────────────────

DraftPicker.propTypes = {
  drafts:   PropTypes.array.isRequired,
  onResume: PropTypes.func.isRequired,
  onFresh:  PropTypes.func.isRequired,
  onClose:  PropTypes.func.isRequired,
};

function DraftPicker({ drafts, onResume, onFresh, onClose }) {
  return (
    <div className="t2t-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="t2t-modal" style={{ maxWidth: 540, width: "95vw" }}>
        <div className="t2t-modal-head">
          <div>
            <div className="t2t-modal-title">Saved Drafts</div>
            <div className="t2t-modal-sub">Resume a previous draft or start fresh</div>
          </div>
          <button className="t2t-icon-btn" onClick={onClose}><i className="bi bi-x-lg" /></button>
        </div>

        <div className="t2t-modal-body" style={{ maxHeight: "55vh", overflowY: "auto" }}>
          {drafts.map((d, idx) => {
            const fd = d.form_data || {};
            const preview = [
              fd.vendor_name && `Vendor: ${fd.vendor_name}`,
              fd.account_group && `Group: ${fd.account_group}`,
              fd.company_code  && `Co: ${fd.company_code}`,
            ].filter(Boolean).join(" · ") || "No fields filled";
            const savedAt = d.last_saved_at ? new Date(d.last_saved_at) : null;
            const filledCount = Object.values(fd).filter((v) => v !== "" && v !== null && v !== undefined).length;
            return (
              <div key={d.id || idx} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 14px", borderBottom: "1px solid #F1F5F9",
                cursor: "pointer", borderRadius: 6, transition: "background 0.1s",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#F8FAFC"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                onClick={() => onResume(d)}
              >
                <div style={{ width: 36, height: 36, borderRadius: 8, background: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <i className="bi bi-file-earmark-text" style={{ color: "#2563EB", fontSize: 16 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>Draft {idx + 1}</div>
                  <div style={{ fontSize: 11, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{preview}</div>
                  <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>
                    {filledCount} field{filledCount !== 1 ? "s" : ""} filled
                    {savedAt && ` · Last saved ${savedAt.toLocaleDateString("en-IN")} ${savedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`}
                  </div>
                </div>
                <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={(e) => { e.stopPropagation(); onResume(d); }}>
                  Resume
                </button>
              </div>
            );
          })}
        </div>

        <div className="t2t-modal-foot">
          <button className="t2t-btn t2t-btn-outline" onClick={onFresh}>
            <i className="bi bi-plus me-1" /> Start Fresh
          </button>
          <button className="t2t-btn t2t-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Review Modal ────────────────────────────────────────────────────────────

// BUG-57: keys + labels must stay in sync with VendorCreationForm field names.
// Shape: { label, fields: [{ key, label }] } — keys mirror VendorCreationForm exactly;
// labels match the visible Field labels on each tab.
const VENDOR_REVIEW_TABS = [
  { label: "1. Initial", fields: [
    { key: "account_group",   label: "Account Group" },
    { key: "company_code",    label: "Company Code" },
    { key: "vendor_name",     label: "Vendor Name" },
    { key: "vendor_name_2",   label: "Vendor Name 2" },
    { key: "vendor_type",     label: "Vendor Type" },
    { key: "vendor_category", label: "Vendor Category" },
  ]},
  { label: "2. General Data", fields: [
    { key: "contact_name",  label: "Contact Person" },
    { key: "contact_phone", label: "Contact Phone" },
    { key: "contact_email", label: "Contact Email" },
    { key: "street",        label: "Street / Address" },
    { key: "city",          label: "City" },
    { key: "state",         label: "State" },
    { key: "pin_code",      label: "Pin Code" },
    { key: "country_code",  label: "Country Code" },
  ]},
  { label: "3. Tax", fields: [
    { key: "pan",          label: "PAN Number" },
    { key: "gstin",        label: "GSTIN" },
    { key: "msme_reg",     label: "MSME Registration No." },
    { key: "cin",          label: "CIN" },
    { key: "tds_code",     label: "TDS Code" },
    { key: "wht_code",     label: "WHT Code" },
    { key: "section_code", label: "Section Code" },
    { key: "vat_number",   label: "VAT Number" },
    { key: "tax_id",       label: "Tax ID" },
  ]},
  { label: "4. Finance", fields: [
    { key: "bank_name",              label: "Bank Name" },
    { key: "bank_account_number",    label: "Account Number" },
    { key: "ifsc_code",              label: "IFSC Code" },
    { key: "bank_account_type",      label: "Account Type" },
    { key: "swift_code",             label: "SWIFT Code" },
    { key: "iban",                   label: "IBAN" },
    { key: "payment_terms",          label: "Payment Terms" },
    { key: "reconciliation_account", label: "Reconciliation A/C" },
    { key: "currency",               label: "Currency" },
  ]},
  { label: "5. Purchasing", fields: [
    { key: "plant_code",     label: "Plant Code" },
    { key: "purchasing_org", label: "Purchasing Organisation" },
    { key: "order_currency", label: "Order Currency" },
    { key: "delivery_terms", label: "Delivery Terms" },
    { key: "incoterms",      label: "Incoterms" },
    { key: "incoterms_2",    label: "Incoterms 2" },
  ]},
  { label: "6. Partner Function", fields: [
    { key: "partner_role",   label: "Partner Role" },
    { key: "partner_number", label: "Partner Number (SAP)" },
    { key: "vendor_notes",   label: "Additional Notes" },
  ]},
];

const VENDOR_REVIEW_KNOWN_KEYS = new Set(
  VENDOR_REVIEW_TABS.flatMap((t) => t.fields.map((f) => f.key))
);

function cleanLabel(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const PRIORITY_STYLES = {
  critical: { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", label: "Critical" },
  high:     { color: "#D97706", bg: "#FFF7ED", border: "#FED7AA", label: "High" },
  medium:   { color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE", label: "Medium" },
  low:      { color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0", label: "Low" },
};

ReviewModal.propTypes = {
  formData:         PropTypes.object.isRequired,
  isVendorCreation: PropTypes.bool.isRequired,
  fields:           PropTypes.array.isRequired,
  fieldLabel:       PropTypes.func.isRequired,
  fieldKey:         PropTypes.func.isRequired,
  priority:         PropTypes.string.isRequired,
  onClose:          PropTypes.func.isRequired,
  onConfirm:        PropTypes.func.isRequired,
};

function ReviewModal({ formData, isVendorCreation, fields, fieldLabel, fieldKey, priority, onClose, onConfirm }) {
  const filledCount = Object.entries(formData).filter(([k, v]) => !k.startsWith("_") && v !== "" && v !== null && v !== undefined).length;
  const ps = PRIORITY_STYLES[priority] || PRIORITY_STYLES.medium;

  return (
    <div className="t2t-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="t2t-modal" style={{ maxWidth: 640, width: "95vw" }}>
        <div className="t2t-modal-head">
          <div>
            <div className="t2t-modal-title">Review Before Submitting</div>
            <div className="t2t-modal-sub">{filledCount} field{filledCount !== 1 ? "s" : ""} filled — please verify before confirming</div>
          </div>
          <button className="t2t-icon-btn" onClick={onClose}><i className="bi bi-x-lg" /></button>
        </div>

        <div className="t2t-modal-body" style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {/* Priority summary row */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, padding: "8px 12px", borderRadius: 6, background: ps.bg, border: `1px solid ${ps.border}` }}>
            <i className="bi bi-flag-fill" style={{ color: ps.color }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: ps.color }}>Priority: {ps.label}</span>
          </div>

          {isVendorCreation ? (
            <>
              {VENDOR_REVIEW_TABS.map((tab) => {
                const rows = tab.fields
                  .map((f) => ({ key: f.key, label: f.label, value: formData[f.key] }))
                  .filter(({ value }) => value !== undefined && value !== null && value !== "");
                if (rows.length === 0) return null;
                return (
                  <div key={tab.label} style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: "var(--t2t-primary)", borderBottom: "1px solid #E2E8F0", paddingBottom: 4, marginBottom: 8 }}>
                      {tab.label}
                    </div>
                    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                      <tbody>
                        {rows.map(({ key, label, value }) => (
                          <tr key={key} style={{ borderBottom: "1px solid #F8FAFC" }}>
                            <td style={{ padding: "5px 8px", color: "#64748B", width: "40%", fontWeight: 500 }}>{label}</td>
                            <td style={{ padding: "5px 8px", fontWeight: 600, wordBreak: "break-all" }}>{String(value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
              {(() => {
                // BUG-57 safety net: surface any filled keys not covered by a tab so future
                // VendorCreationForm fields don't silently disappear from review.
                const otherRows = Object.entries(formData).filter(([k, v]) =>
                  !k.startsWith("_") &&
                  !VENDOR_REVIEW_KNOWN_KEYS.has(k) &&
                  v !== undefined && v !== null && v !== ""
                );
                if (otherRows.length === 0) return null;
                return (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: "var(--t2t-primary)", borderBottom: "1px solid #E2E8F0", paddingBottom: 4, marginBottom: 8 }}>
                      Other Fields
                    </div>
                    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                      <tbody>
                        {otherRows.map(([key, value]) => (
                          <tr key={key} style={{ borderBottom: "1px solid #F8FAFC" }}>
                            <td style={{ padding: "5px 8px", color: "#64748B", width: "40%", fontWeight: 500 }}>{cleanLabel(key)}</td>
                            <td style={{ padding: "5px 8px", fontWeight: 600, wordBreak: "break-all" }}>{String(value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </>
          ) : (
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <tbody>
                {fields.map((f) => {
                  const k = fieldKey(f);
                  const v = formData[k];
                  if (v === undefined || v === null || v === "") return null;
                  return (
                    <tr key={k} style={{ borderBottom: "1px solid #F8FAFC" }}>
                      <td style={{ padding: "5px 8px", color: "#64748B", width: "40%", fontWeight: 500 }}>{fieldLabel(f)}</td>
                      <td style={{ padding: "5px 8px", fontWeight: 600, wordBreak: "break-all" }}>{String(v)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {filledCount === 0 && (
            <div style={{ textAlign: "center", padding: "24px 0", color: "#94A3B8", fontSize: 13 }}>
              No fields filled yet.
            </div>
          )}
        </div>

        <div className="t2t-modal-foot">
          <button className="t2t-btn t2t-btn-outline" onClick={onClose}>← Edit</button>
          <button className="t2t-btn t2t-btn-primary" onClick={onConfirm}>
            <i className="bi bi-check2-circle me-1" /> Confirm &amp; Submit
          </button>
        </div>
      </div>
    </div>
  );
}

export default T2TSubmit;
